import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Check, X, Search, Loader2 } from 'lucide-react';
import { cn } from '../../utils/cn';

/**
 * MultiSelectCheckboxDropdown
 * A sleek input-field style multi-select dropdown with checkboxes.
 * Replaces static scrollable list boxes with an interactive dropdown field.
 */
const MultiSelectCheckboxDropdown = ({
  options = [],
  value = [],
  onChange,
  placeholder = 'Select options...',
  loading = false,
  loadingText = 'Loading...',
  emptyText = 'No options available',
  disabled = false,
  showSearch = true,
  searchPlaceholder = 'Search...',
  id,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedList = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  // Normalize options to { id, name, abbreviation }
  const normalizedOptions = useMemo(() => {
    return options.map((opt, idx) => {
      if (typeof opt === 'string') {
        return { id: opt, name: opt, abbreviation: null };
      }
      return {
        id: opt.id || opt.type_id || opt.name || `opt-${idx}`,
        name: opt.name || opt.label || String(opt),
        abbreviation: opt.abbreviation || null,
      };
    });
  }, [options]);

  // Summary of selected items using abbreviation if available
  const selectedSummary = useMemo(() => {
    if (!selectedList.length) return '';
    return selectedList
      .map((sel) => {
        const match = normalizedOptions.find(
          (opt) => opt.name === sel || opt.id === sel
        );
        return match?.abbreviation || sel;
      })
      .join(', ');
  }, [selectedList, normalizedOptions]);

  // Filtered options by search term (matches either name or abbreviation)
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return normalizedOptions;
    const term = searchTerm.toLowerCase().trim();
    return normalizedOptions.filter((opt) => {
      const matchName = opt.name.toLowerCase().includes(term);
      const matchAbbr = opt.abbreviation
        ? opt.abbreviation.toLowerCase().includes(term)
        : false;
      return matchName || matchAbbr;
    });
  }, [normalizedOptions, searchTerm]);

  // Outside click & escape listener to close dropdown
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Auto focus search field when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 40);
    } else if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  const toggleOption = (name) => {
    if (disabled) return;
    const updated = selectedList.includes(name)
      ? selectedList.filter((item) => item !== name)
      : [...selectedList, name];
    onChange?.(updated);
  };

  const handleSelectAll = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const visibleNames = filteredOptions.map((o) => o.name);
    const combined = Array.from(new Set([...selectedList, ...visibleNames]));
    onChange?.(combined);
  };

  const handleClearAll = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onChange?.([]);
  };

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      {/* Trigger Field */}
      <button
        type="button"
        id={id}
        disabled={disabled || loading}
        onClick={() => setIsOpen((prev) => !prev)}
        className={cn(
          'flex h-9 sm:h-10 w-full items-center justify-between rounded-md border border-input bg-background px-2.5 sm:px-3 py-2 text-xs sm:text-sm text-left transition-colors cursor-pointer',
          'hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
          disabled && 'opacity-50 cursor-not-allowed bg-gray-50',
          isOpen && 'border-blue-500 ring-2 ring-blue-500/20'
        )}
      >
        <div className="flex items-center gap-1.5 flex-1 min-w-0 pr-1.5">
          {loading ? (
            <span className="flex items-center gap-2 text-xs text-gray-400 truncate">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600 shrink-0" />
              {loadingText}
            </span>
          ) : selectedList.length === 0 ? (
            <span className="text-gray-400 truncate text-xs sm:text-sm">{placeholder}</span>
          ) : (
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 shrink-0">
                {selectedList.length} selected
              </span>
              <span
                className="truncate text-gray-800 text-xs sm:text-sm font-medium cursor-help"
                title={selectedList.join(', ')}
              >
                {selectedSummary}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0 text-gray-400">
          {selectedList.length > 0 && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClearAll}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleClearAll(e);
              }}
              title="Clear all"
              className="p-0.5 hover:text-gray-700 rounded hover:bg-gray-100 transition-colors cursor-pointer mr-0.5"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown
            className={cn(
              'w-4 h-4 opacity-50 transition-transform duration-200 shrink-0',
              isOpen && 'rotate-180 opacity-100 text-blue-600'
            )}
          />
        </div>
      </button>

      {/* Floating Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 rounded-lg border border-gray-200 bg-white text-popover-foreground shadow-xl animate-in fade-in-0 duration-100 overflow-hidden min-w-[220px]">
          {/* Search Bar & Quick Action Controls */}
          <div className="p-2 border-b border-gray-100 bg-gray-50/70 space-y-1.5">
            {showSearch && normalizedOptions.length > 4 && (
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full pl-8 pr-7 py-1.5 text-xs bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}

            <div className="flex items-center justify-between px-0.5 text-[11px] text-gray-500">
              <span className="font-medium text-gray-600">
                {selectedList.length} of {normalizedOptions.length} selected
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-blue-600 hover:text-blue-800 font-semibold hover:underline cursor-pointer"
                >
                  Select All
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={handleClearAll}
                  disabled={selectedList.length === 0}
                  className="text-gray-500 hover:text-red-600 disabled:opacity-40 disabled:hover:text-gray-500 font-medium hover:underline cursor-pointer"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          {/* Options Checklist */}
          <div className="max-h-60 overflow-y-auto p-1 divide-y divide-gray-50">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-xs text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                <span>{loadingText}</span>
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="py-6 text-center text-xs text-gray-400">
                {searchTerm ? 'No matching options found' : emptyText}
              </div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = selectedList.includes(opt.name);
                return (
                  <label
                    key={opt.id}
                    className={cn(
                      'flex items-center gap-2.5 px-2.5 py-2 rounded-md text-xs sm:text-sm cursor-pointer transition-colors select-none',
                      isSelected ? 'bg-blue-50/80 text-blue-900 font-medium' : 'text-gray-700 hover:bg-gray-50'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleOption(opt.name)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 shrink-0 cursor-pointer"
                    />
                    <span
                      className="truncate flex-1 flex items-center gap-1.5 min-w-0"
                      title={opt.name}
                    >
                      {/* Display only abbreviation - hover tooltip shows full name */}
                      <span className="font-semibold text-gray-900 tracking-wide text-xs sm:text-sm">
                        {opt.abbreviation || opt.name}
                      </span>
                      {/* Full name commented out for now - can be re-enabled whenever needed: */}
                      {/* <span className="text-xs text-gray-400 truncate">({opt.name})</span> */}
                    </span>
                    {isSelected && (
                      <Check className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                    )}
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectCheckboxDropdown;
